// connectionConfigs.ts — archetype-aware copy for the Connections Layer.
//
// Every Den-chassis feature gets reused across 14+ archetypes (15 today).
// This config keeps the user-facing copy together so the drawer, settings
// page, and suggested-actions surfaces all read from one source. The
// founder slots ship with verbatim copy from Saul; the other 13 archetypes
// are seeded with [ARCHETYPE-COPY-PENDING] placeholders that getCopyForUser
// transparently replaces with defaultCopy at render time, so users never
// see the raw [PENDING] string.
//
// Tier 1 (essential): twitter, github, linkedin, medium    — enabled
// Tier 2 (group):     slack, telegram, indiehackers, hackernews
//                                                          — visible-but-disabled
// Tier 3 (community/visual): substack, bluesky, reddit, devto, rss
//                            youtube, instagram, tiktok, threads
//                                                          — hidden under "More"
//
// FOX-BIBLE §XXIII compliance: every user-visible string in this file is
// hand-written, no underbars between words, no internal field keys.

import {
  Twitter,
  Github,
  Linkedin,
  Youtube,
  Instagram,
  Rss,
  Slack,
  Send,
  Hash,
  BookOpen,
  MessageSquare,
  Cloud,
  Camera,
  Music,
  type LucideIcon,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────

export type Archetype =
  | "founder"
  | "ceo_small"
  | "executive"
  | "politician"
  | "physician"
  | "academic"
  | "actor"
  | "athlete"
  | "author"
  | "journalist"
  | "religious"
  | "military"
  | "judicial"
  | "activist"
  | "artist";

export type Tier = "essential" | "group" | "visual" | "community";

export type Availability =
  | "live"
  | "coming_soon"
  | "pending_review"
  | "unavailable_upstream";

export interface PlatformCopy {
  whatYouGet: string;   // line 1: specific data we'll show
  cohortSignal: string; // line 2: directional aspirational framing
}

export interface PlatformConfig {
  name: string;
  tier: Tier;
  enabled: boolean;
  multiAccount: boolean;
  icon: LucideIcon;
  scopes: string[];
  privacyFloor: string;              // same across archetypes
  defaultCopy: PlatformCopy;         // fallback when archetype slot is pending
  byArchetype: Partial<Record<Archetype, PlatformCopy>>;
  availability?: Availability;       // honest disabled-state grading; absent = legacy "Coming next week" fallback.
  pendingReview?: boolean;           // OAuth app awaiting provider approval (e.g. LinkedIn).
  pendingReviewMessage?: string;     // tooltip copy shown next to the "Review pending" badge.
  comingSoonMessage?: string;        // tooltip copy shown next to the "Coming soon" badge (overrides the generic message).
}

// ── Helpers ──────────────────────────────────────────────────────────

const PENDING_RE = /\[[A-Z_-]+-PENDING\]/;

const PRIVACY_FLOOR_OAUTH =
  "Read-only. We never post for you, never DM anyone, never see your private messages.";
const PRIVACY_FLOOR_INTEGRATION_TOKEN =
  "Read-only. The token only authorizes us to read public profile and post metadata. You can revoke it any time from your account settings on the platform.";

// Helper to build the byArchetype block with [ARCHETYPE-COPY-PENDING]
// placeholders for every archetype except those passed in `filled`. The
// `filled` object's slots are kept verbatim.
function archetypeBlock(filled: Partial<Record<Archetype, PlatformCopy>>): Partial<Record<Archetype, PlatformCopy>> {
  const ARCHETYPES: Archetype[] = [
    "founder", "ceo_small", "executive", "politician", "physician",
    "academic", "actor", "athlete", "author", "journalist", "religious",
    "military", "judicial", "activist", "artist",
  ];
  const out: Partial<Record<Archetype, PlatformCopy>> = {};
  for (const a of ARCHETYPES) {
    if (filled[a]) {
      out[a] = filled[a];
    } else {
      const slug = a.toUpperCase().replace(/_/g, "-");
      out[a] = {
        whatYouGet: `[${slug}-COPY-PENDING]`,
        cohortSignal: `[${slug}-COPY-PENDING]`,
      };
    }
  }
  return out;
}

// ── Platform configs ─────────────────────────────────────────────────

export const PLATFORM_CONFIGS: Record<string, PlatformConfig> = {
  // ── Tier 1: ESSENTIALS ────────────────────────────────────────────

  twitter: {
    name: "Twitter / X",
    tier: "essential",
    enabled: false,
    availability: "coming_soon",
    comingSoonMessage: "Coming soon — X dev account migration in progress",
    multiAccount: true,
    icon: Twitter,
    scopes: ["tweet.read", "users.read", "follows.read"],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "post velocity, reply rate, follower trend",
      cohortSignal: "Showing up consistently builds the audience that listens when you launch.",
    },
    byArchetype: archetypeBlock({
      founder: {
        whatYouGet: "post velocity, replies you send vs receive, follower trend, and which threads got you noticed",
        cohortSignal: "Founders who launch well don't show up on launch day — they're already familiar by then. Daily presence in the months before is the muscle, not the moment.",
      },
    }),
  },

  github: {
    name: "GitHub",
    tier: "essential",
    enabled: true,
    availability: "live",
    multiAccount: true,
    icon: Github,
    scopes: ["read:user", "user:email", "public_repo"],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "commits per week, top repo traffic, star trend, contribution heatmap",
      cohortSignal: "A public commit cadence is the cheapest signal that the work is real and moving.",
    },
    byArchetype: archetypeBlock({
      founder: {
        whatYouGet: "commits per week, top repo traffic (clones + views), star trend, contribution heatmap",
        cohortSignal: "Shipping is the proof. Public commit cadence tells journalists, investors, and would-be users that the thing is real and moving — silent repos read as abandoned even when they're not.",
      },
    }),
  },

  linkedin: {
    name: "LinkedIn",
    tier: "essential",
    enabled: false,
    pendingReview: true,
    pendingReviewMessage: "LinkedIn approval in queue — automatic when LinkedIn clears review.",
    availability: "pending_review",
    multiAccount: false,
    icon: Linkedin,
    scopes: ["openid", "profile", "email"],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "post cadence, comment engagement, follower trend, articles published",
      cohortSignal: "Decision-makers check LinkedIn before they reply to anything else.",
    },
    byArchetype: archetypeBlock({
      founder: {
        whatYouGet: "post cadence, comment engagement, follower trend, articles published",
        cohortSignal: "B2B buyers and journalists check LinkedIn before they reply. Weekly long-form there does what Twitter can't — it builds the version of you that decision-makers want to see.",
      },
    }),
  },

  medium: {
    name: "Medium",
    tier: "essential",
    enabled: false,
    availability: "unavailable_upstream",
    multiAccount: false,
    icon: BookOpen,
    scopes: ["basicProfile"],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "posts per month, claps, top stories, follower trend",
      cohortSignal: "Long-form posts are what AI search engines cite. One thoughtful piece a month builds substrate that compounds.",
    },
    byArchetype: archetypeBlock({
      founder: {
        whatYouGet: "posts per month, claps, top stories, follower trend",
        cohortSignal: "Long-form is what LLMs cite. One thoughtful piece a month builds the substrate AI search engines pull from when someone asks about your space.",
      },
    }),
  },

  // ── Tier 2: GROUPS (visible but disabled) ─────────────────────────

  slack: {
    name: "Slack",
    tier: "group",
    enabled: false,
    multiAccount: true,
    icon: Slack,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "channels you post in, message cadence, top reactions",
      cohortSignal: "The community spaces you show up in shape the network that opens doors.",
    },
    byArchetype: archetypeBlock({}),
  },

  telegram: {
    name: "Telegram",
    tier: "group",
    enabled: false,
    multiAccount: false,
    icon: Send,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "channel posts, subscriber trend, top engaged messages",
      cohortSignal: "Direct broadcast channels build the audience that hears about your launches first.",
    },
    byArchetype: archetypeBlock({}),
  },

  indiehackers: {
    name: "Indie Hackers",
    tier: "group",
    enabled: false,
    multiAccount: false,
    icon: MessageSquare,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "posts, comments, milestones, top engaged threads",
      cohortSignal: "The right-fit community for early-stage builders is the smallest stage with the highest signal.",
    },
    byArchetype: archetypeBlock({}),
  },

  hackernews: {
    name: "Hacker News",
    tier: "group",
    enabled: false,
    multiAccount: false,
    icon: Hash,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "submissions, comments, karma trend, top stories",
      cohortSignal: "One post on the front page can change a quarter. Showing up regularly is how you get the chance.",
    },
    byArchetype: archetypeBlock({}),
  },

  // ── Tier 3: COMMUNITY (hidden under "More") ───────────────────────

  substack: {
    name: "Substack",
    tier: "community",
    enabled: false,
    multiAccount: true,
    icon: BookOpen,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "posts per month, subscriber trend, open rate, top issues",
      cohortSignal: "An owned email list is the most durable distribution you can build.",
    },
    byArchetype: archetypeBlock({}),
  },

  bluesky: {
    name: "Bluesky",
    tier: "community",
    enabled: false,
    multiAccount: true,
    icon: Cloud,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "post velocity, follower trend, replies, top posts",
      cohortSignal: "An emerging platform rewards early consistent presence with outsized reach.",
    },
    byArchetype: archetypeBlock({}),
  },

  reddit: {
    name: "Reddit",
    tier: "community",
    enabled: false,
    multiAccount: true,
    icon: MessageSquare,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "posts, comments, karma, top subreddits you participate in",
      cohortSignal: "Niche subreddits often outperform mainstream channels for the right kind of audience.",
    },
    byArchetype: archetypeBlock({}),
  },

  devto: {
    name: "DEV.to",
    tier: "community",
    enabled: false,
    multiAccount: false,
    icon: BookOpen,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_INTEGRATION_TOKEN,
    defaultCopy: {
      whatYouGet: "posts, reactions, top stories, follower trend",
      cohortSignal: "A friendly developer community amplifies thoughtful long-form when other platforms ignore it.",
    },
    byArchetype: archetypeBlock({}),
  },

  rss: {
    name: "RSS Feed",
    tier: "community",
    enabled: false,
    multiAccount: true,
    icon: Rss,
    scopes: [],
    privacyFloor:
      "Read-only. We poll the public feed URL on a schedule. No credentials are stored.",
    defaultCopy: {
      whatYouGet: "post cadence and titles from any blog you list",
      cohortSignal: "An RSS feed turns any owned site into a measurable cadence signal.",
    },
    byArchetype: archetypeBlock({}),
  },

  // ── Tier 3: VISUAL (hidden under "More") ──────────────────────────

  youtube: {
    name: "YouTube",
    tier: "visual",
    enabled: false,
    multiAccount: false,
    icon: Youtube,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "uploads per month, watch time trend, subscriber trend, top videos",
      cohortSignal: "Long-form video is the single highest-trust medium when it works.",
    },
    byArchetype: archetypeBlock({}),
  },

  instagram: {
    name: "Instagram",
    tier: "visual",
    enabled: false,
    multiAccount: true,
    icon: Instagram,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "posts per week, follower trend, reach, top posts",
      cohortSignal: "Visual presence carries weight where text-only channels read as faceless.",
    },
    byArchetype: archetypeBlock({}),
  },

  tiktok: {
    name: "TikTok",
    tier: "visual",
    enabled: false,
    multiAccount: true,
    icon: Music,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "posts per week, view trend, follower trend, top videos",
      cohortSignal: "Algorithmic discovery there can take you from zero to known in a single hit.",
    },
    byArchetype: archetypeBlock({}),
  },

  threads: {
    name: "Threads",
    tier: "visual",
    enabled: false,
    multiAccount: true,
    icon: Camera,
    scopes: [],
    privacyFloor: PRIVACY_FLOOR_OAUTH,
    defaultCopy: {
      whatYouGet: "posts per week, follower trend, replies, top threads",
      cohortSignal: "An emerging text-first platform inside a giant network gives early presence outsized reach.",
    },
    byArchetype: archetypeBlock({}),
  },
};

// ── Public API ────────────────────────────────────────────────────────

/**
 * Resolve the user-facing copy for a (platform, archetype) pair.
 *
 * Falls back to defaultCopy when:
 *   - platform key is unknown
 *   - archetype is null/unset
 *   - archetype slot still contains [ARCHETYPE-COPY-PENDING] sentinel
 *
 * The pending-fallback rule is what lets us ship the entire 285-cell
 * matrix today and fill real archetype copy archetype-by-archetype in
 * future sessions without ever leaking the placeholder to a user.
 */
export function getCopyForUser(platform: string, archetype: Archetype | null | undefined): PlatformCopy {
  const config = PLATFORM_CONFIGS[platform];
  if (!config) return { whatYouGet: "", cohortSignal: "" };
  if (!archetype) return config.defaultCopy;

  const archetypeCopy = config.byArchetype[archetype];
  if (!archetypeCopy) return config.defaultCopy;

  if (PENDING_RE.test(archetypeCopy.whatYouGet) || PENDING_RE.test(archetypeCopy.cohortSignal)) {
    return config.defaultCopy;
  }
  return archetypeCopy;
}

/**
 * The four Tier-1 platform keys that count toward the Connections progress
 * bar and the connect_essentials suggested action. Centralized so the
 * drawer header, the suggested-action firing condition, and any future
 * "X of 4 essentials" callout all read the same source of truth.
 */
export const ESSENTIAL_PLATFORM_KEYS = ["twitter", "github", "linkedin", "medium"] as const;

/**
 * Order of Tier-2 platforms inside the GROUPS section. Hand-curated.
 */
export const GROUP_PLATFORM_KEYS = ["slack", "telegram", "indiehackers", "hackernews"] as const;

/**
 * Order of Tier-3 platforms inside the "More" accordion. Hand-curated:
 * community list first (text-driven), then visual list.
 */
export const MORE_PLATFORM_KEYS = [
  "substack", "bluesky", "reddit", "devto", "rss",
  "youtube", "instagram", "tiktok", "threads",
] as const;
